import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/authSlice';
import { toast } from 'react-toastify';
import './Header.css';

const NAV = [
  { label: 'Men',        path: '/products/men',                  sub: ['T-Shirts','Shirts','Jeans','Trousers','Blazers','Jackets','Sportswear','Footwear'] },
  { label: 'Women',      path: '/products/women',                sub: ['Dresses','Tops','Sarees','Kurtis','Leggings','Skirts','Ethnic Wear','Footwear'] },
  { label: 'Kids',       path: '/products/kids',                 sub: ['Boys Clothing','Girls Clothing','Kids Footwear','School Bags'] },
  { label: 'Accessories',path: '/products?category=accessories', sub: ['Handbags','Watches','Sunglasses','Jewellery','Belts'] },
  { label: 'Beauty',     path: '/products/beauty',               sub: ['Skincare','Makeup','Haircare','Fragrances'] },
  { label: 'Sale 🔥',   path: '/products?sale=true',            sub: [], hot: true },
];

/* ─── SVG icon components ─── */
const Icon = ({ d, d2, type = 'path', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {type === 'path'   && <path d={d} />}
    {type === 'circle' && <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></>}
    {d2 && <path d={d2} />}
  </svg>
);

const SearchIcon   = () => <Icon type="circle" />;
const HeartIcon    = () => <Icon d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />;
const BagIcon      = () => <Icon d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" d2="M3 6h18M16 10a4 4 0 0 1-8 0" />;
const UserIcon     = () => <Icon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" d2="M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />;
const ChevronIcon  = () => <Icon d="m6 9 6 6 6-6" size={16} />;
const ChevronRight = () => <Icon d="m9 18 6-6-6-6" size={16} />;
const CloseIcon    = () => <Icon d="M18 6 6 18M6 6l12 12" />;
const OrderIcon    = () => <Icon d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" d2="M8 2h8v4H8z" />;
const GridIcon     = () => <Icon d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />;
const LogoutIcon   = () => <Icon d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" d2="M16 17l5-5-5-5M21 12H9" />;

export default function Header() {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const location  = useLocation();
  const isLightPage = location.pathname.startsWith('/profile');
  const { isAuthenticated, user } = useSelector(s => s.auth);
  const cartCount     = useSelector(s => s.cart.items.reduce((t, i) => t + i.quantity, 0));
  const wishlistCount = useSelector(s => s.wishlist.items.length);

  const [scrolled,     setScrolled]     = useState(false);
  const [activeNav,    setActiveNav]    = useState(null);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [profileOpen,  setProfileOpen]  = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery(''); setSearchOpen(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    toast.success('See you soon! 👋');
    navigate('/');
    setProfileOpen(false);
  };

  return (
    <>
      <header className={`hdr${scrolled ? ' hdr--solid' : ''}${isLightPage ? ' hdr--light' : ''}`}>
        <div className="hdr-inner">

          {/* ── Logo ── */}
          <Link to="/" className="hdr-logo" onClick={() => setMobileOpen(false)}>
            <span className="logo-huge">HUGE</span>
            <span className="logo-dot">.</span>
          </Link>

          {/* ── Desktop nav ── */}
          <nav className="hdr-nav">
            {NAV.map(item => (
              <div key={item.label} className="nav-item"
                onMouseEnter={() => setActiveNav(item.label)}
                onMouseLeave={() => setActiveNav(null)}>
                <Link to={item.path} className={`nav-link${item.hot ? ' nav-link--hot' : ''}`}>
                  {item.label}
                </Link>
                {item.sub.length > 0 && activeNav === item.label && (
                  <div className="nav-dropdown">
                    {item.sub.map(s => (
                      <Link key={s} to={`${item.path}?sub=${s.toLowerCase().replace(/ /g,'-')}`}
                        className="nav-dropdown-item">{s}</Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* ── Actions ── */}
          <div className="hdr-actions">

            <button className="hdr-btn" onClick={() => setSearchOpen(v => !v)} aria-label="Search">
              <SearchIcon />
            </button>

            <button className="hdr-btn hdr-btn--badge" onClick={() => navigate('/wishlist')} aria-label="Wishlist">
              <HeartIcon />
              {wishlistCount > 0 && <span className="hdr-badge">{wishlistCount > 9 ? '9+' : wishlistCount}</span>}
            </button>

            <button className="hdr-btn hdr-btn--badge" onClick={() => navigate('/cart')} aria-label="Cart">
              <BagIcon />
              {cartCount > 0 && <span className="hdr-badge hdr-badge--cart">{cartCount > 9 ? '9+' : cartCount}</span>}
            </button>

            {isAuthenticated ? (
              <div className="profile-wrap"
                onMouseEnter={() => setProfileOpen(true)}
                onMouseLeave={() => setProfileOpen(false)}>
                <button className="profile-trigger">
                  <span className="profile-avatar">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
                  <span className="profile-name-text">{user?.name?.split(' ')[0]}</span>
                  <ChevronIcon />
                </button>

                {profileOpen && (
                  <div className="profile-menu">
                    <div className="pm-header">
                      <div className="pm-avatar-lg">{user?.name?.[0]?.toUpperCase()}</div>
                      <div className="pm-info">
                        <p className="pm-name">{user?.name}</p>
                        <p className="pm-email">{user?.email}</p>
                      </div>
                    </div>
                    <div className="pm-divider" />
                    {user?.role === 'ADMIN' && (
                      <Link to="/admin" className="pm-item pm-item--admin">
                        <GridIcon /> Admin Panel
                      </Link>
                    )}
                    <Link to="/profile" className="pm-item"><UserIcon /> My Profile</Link>
                    <Link to="/orders"  className="pm-item"><OrderIcon /> My Orders</Link>
                    <Link to="/wishlist" className="pm-item"><HeartIcon /> Wishlist</Link>
                    <div className="pm-divider" />
                    <button className="pm-item pm-item--danger" onClick={handleLogout}>
                      <LogoutIcon /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn btn-primary hdr-signin">Sign In</Link>
            )}

            <button className="hdr-hamburger" onClick={() => setMobileOpen(v => !v)} aria-label="Menu">
              <span className={`hbg${mobileOpen ? ' hbg--open' : ''}`}>
                <span /><span /><span />
              </span>
            </button>
          </div>
        </div>

        {/* ── Search overlay ── */}
        {searchOpen && (
          <div className="search-bar">
            <form onSubmit={handleSearch} className="search-form">
              <span className="search-icon"><SearchIcon /></span>
              <input ref={searchRef} type="text" placeholder="Search brands, styles, categories…"
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="search-input" />
              {searchQuery && (
                <button type="button" className="search-clear" onClick={() => setSearchQuery('')}>
                  <CloseIcon />
                </button>
              )}
            </form>
            <button className="search-dismiss" onClick={() => setSearchOpen(false)}>
              <CloseIcon />
            </button>
          </div>
        )}
      </header>

      {/* ── Mobile menu ── */}
      <div className={`mobile-menu${mobileOpen ? ' mobile-menu--open' : ''}`}>
        <nav className="mobile-nav">
          {NAV.map(item => (
            <Link key={item.label} to={item.path}
              className={`mobile-nav-item${item.hot ? ' mobile-nav-item--hot' : ''}`}
              onClick={() => setMobileOpen(false)}>
              {item.label} <ChevronRight />
            </Link>
          ))}
        </nav>
        <div className="mobile-footer">
          {isAuthenticated ? (
            <>
              <Link to="/profile" className="btn btn-outline w-full" onClick={() => setMobileOpen(false)}>My Profile</Link>
              <button className="btn btn-ghost w-full" style={{ color: 'var(--huge-red)' }} onClick={handleLogout}>Sign Out</button>
            </>
          ) : (
            <Link to="/login" className="btn btn-primary w-full" onClick={() => setMobileOpen(false)}>Sign In / Register</Link>
          )}
        </div>
      </div>
      {mobileOpen && <div className="mobile-backdrop" onClick={() => setMobileOpen(false)} />}
    </>
  );
}
